import type { Box, Vec } from "./geometry";

export const MIN_ZOOM = 0.04;
export const MAX_ZOOM = 24;

/** Maps world coordinates to the screen: screen = world * z + (x, y). */
export class Camera {
  x = 0;
  y = 0;
  z = 1;
  /** viewport size in CSS pixels */
  width = 1;
  height = 1;
  private listeners = new Set<() => void>();
  private anim: number | null = null;

  toWorld(p: Vec): Vec {
    return { x: (p.x - this.x) / this.z, y: (p.y - this.y) / this.z };
  }

  toScreen(p: Vec): Vec {
    return { x: p.x * this.z + this.x, y: p.y * this.z + this.y };
  }

  /** Visible world rectangle. */
  view(margin = 0): Box {
    const m = margin / this.z;
    return { x: -this.x / this.z - m, y: -this.y / this.z - m, w: this.width / this.z + 2 * m, h: this.height / this.z + 2 * m };
  }

  /** When set, the middle of the view stays over this world box (a paged board keeps its pages in sight). */
  limit: (() => Box | null) | null = null;

  set(x: number, y: number, z: number, cancelAnim = true): void {
    if (cancelAnim) this.stop();
    z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    const lim = this.limit?.();
    if (lim) {
      const cx = (this.width / 2 - x) / z;
      const cy = (this.height / 2 - y) / z;
      const kx = Math.min(lim.x + lim.w, Math.max(lim.x, cx));
      const ky = Math.min(lim.y + lim.h, Math.max(lim.y, cy));
      if (kx !== cx) x = this.width / 2 - kx * z;
      if (ky !== cy) y = this.height / 2 - ky * z;
    }
    if (x === this.x && y === this.y && z === this.z) return;
    this.x = x;
    this.y = y;
    this.z = z;
    this.emit();
  }

  panBy(dx: number, dy: number): void {
    this.set(this.x + dx, this.y + dy, this.z);
  }

  /** Zoom by `factor` keeping the screen point `at` fixed. */
  zoomAt(at: Vec, factor: number): void {
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, this.z * factor));
    const k = z / this.z;
    this.set(at.x - (at.x - this.x) * k, at.y - (at.y - this.y) * k, z);
  }

  resize(w: number, h: number): void {
    if (w === this.width && h === this.height) return;
    this.width = Math.max(1, w);
    this.height = Math.max(1, h);
    this.emit();
  }

  /** Screen space covered by toolbars, kept clear when fitting content into view. */
  insets = { left: 0, right: 0, top: 0, bottom: 0 };

  /** Camera that fits `box` into the free part of the viewport with `pad` screen pixels around it. */
  fitting(box: Box, pad = 60, maxZoom = 2): { x: number; y: number; z: number } {
    const w = Math.max(box.w, 1);
    const h = Math.max(box.h, 1);
    const i = this.insets;
    const availW = Math.max(80, this.width - i.left - i.right - pad * 2);
    const availH = Math.max(80, this.height - i.top - i.bottom - pad * 2);
    const z = Math.min(maxZoom, Math.max(MIN_ZOOM, Math.min(availW / w, availH / h)));
    const cx = i.left + (this.width - i.left - i.right) / 2;
    const cy = i.top + (this.height - i.top - i.bottom) / 2;
    return { x: cx - (box.x + w / 2) * z, y: cy - (box.y + h / 2) * z, z };
  }

  animateTo(target: { x: number; y: number; z: number }, ms = 320): void {
    this.stop();
    const from = { x: this.x, y: this.y, z: this.z };
    const t0 = performance.now();
    // interpolate zoom logarithmically so the motion feels even
    const lz0 = Math.log(from.z);
    const lz1 = Math.log(target.z);
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / ms);
      const e = 1 - Math.pow(1 - t, 3);
      const z = Math.exp(lz0 + (lz1 - lz0) * e);
      // keep the world point under the screen centre moving linearly
      const cx = this.width / 2;
      const cy = this.height / 2;
      const w0 = { x: (cx - from.x) / from.z, y: (cy - from.y) / from.z };
      const w1 = { x: (cx - target.x) / target.z, y: (cy - target.y) / target.z };
      const wx = w0.x + (w1.x - w0.x) * e;
      const wy = w0.y + (w1.y - w0.y) * e;
      this.set(cx - wx * z, cy - wy * z, z, false);
      if (t < 1) this.anim = requestAnimationFrame(step);
      else this.anim = null;
    };
    this.anim = requestAnimationFrame(step);
  }

  stop(): void {
    if (this.anim !== null) cancelAnimationFrame(this.anim);
    this.anim = null;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}
